// frontend/src/components/ui/SavedPdfReportsModal.tsx
import React, { useState, useEffect } from 'react';
import { FileText, Download, Trash2, Eye, X, FileCheck, Search, Calendar } from 'lucide-react';
import { SavedPdfReport, getSavedPdfReports, deletePdfReport, clearAllPdfReports } from '../../utils/pdfStorage';
import { hapticFeedback } from '../../utils/nativeBridge';
import { useLanguage } from '../../contexts/LanguageContext';

interface SavedPdfReportsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectReport: (report: SavedPdfReport) => void;
}

export const SavedPdfReportsModal: React.FC<SavedPdfReportsModalProps> = ({
    isOpen,
    onClose,
    onSelectReport
}) => {
    const [reports, setReports] = useState<SavedPdfReport[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const { lang } = useLanguage();
    const isTurkish = lang === 'tr';

    useEffect(() => {
        if (isOpen) {
            setReports(getSavedPdfReports());
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleDelete = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        hapticFeedback('medium');
        const updated = deletePdfReport(id);
        setReports(updated);
    };

    const handleClearAll = () => {
        if (window.confirm(isTurkish ? 'Tüm kaydedilmiş PDF rapor geçmişini silmek istediğinize emin misiniz?' : 'Are you sure you want to clear all saved PDF report history?')) {
            hapticFeedback('heavy');
            clearAllPdfReports();
            setReports([]);
        }
    };

    const filteredReports = reports.filter(r => {
        const query = searchQuery.toLowerCase();
        return (
            (r.name && r.name.toLowerCase().includes(query)) ||
            (r.cargoSummary && r.cargoSummary.toLowerCase().includes(query)) ||
            (r.projectDescription && r.projectDescription.toLowerCase().includes(query)) ||
            (r.date && r.date.toLowerCase().includes(query))
        );
    });

    return (
        <div className="fixed inset-0 z-[125] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
            <div className="relative w-full max-w-lg sm:max-w-xl bg-white dark:bg-[#0D111C] rounded-3xl p-4 sm:p-6 shadow-2xl border border-slate-200 dark:border-white/10 flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200">
                {/* Top Header */}
                <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-orange-500/10 text-orange-600 dark:text-orange-400 flex items-center justify-center border border-orange-500/20">
                            <FileText className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                                {isTurkish ? 'PDF Rapor Geçmişim' : 'My PDF Report History'}
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                {isTurkish ? `Kaydedilmiş (${reports.length}) adet rapor bulundu` : `${reports.length} saved report${reports.length === 1 ? '' : 's'} found`}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={() => { hapticFeedback('light'); onClose(); }}
                        className="w-9 h-9 rounded-full bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-white/20 transition-all flex items-center justify-center focus:outline-none"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Search Bar */}
                {reports.length > 0 && (
                    <div className="mt-3 relative">
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder={isTurkish ? 'Rapor ismi, yük veya tarih ara...' : 'Search by report name, cargo or date...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:border-orange-500 transition-colors"
                        />
                    </div>
                )}

                {/* Report Cards List */}
                <div className="flex-1 overflow-y-auto my-3 space-y-2.5 pr-1 min-h-[220px]">
                    {reports.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-center text-slate-400 gap-2">
                            <FileCheck className="w-12 h-12 text-slate-300 dark:text-slate-600 stroke-[1.5]" />
                            <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{isTurkish ? 'Henüz kaydedilmiş PDF raporunuz bulunmuyor.' : 'You do not have any saved PDF reports yet.'}</p>
                            <p className="text-xs text-slate-400 dark:text-slate-500 max-w-xs">{isTurkish ? 'Hesaplama yaptıktan sonra "Download PDF" butonuna bastığınızda raporlarınız otomatik buraya eklenir.' : 'After a calculation, reports are added here automatically when you select "Download PDF".'}</p>
                        </div>
                    ) : filteredReports.length === 0 ? (
                        <div className="py-12 text-center text-slate-400 text-xs font-medium">
                            {isTurkish ? 'Arama kriterine uygun rapor bulunamadı.' : 'No reports match your search.'}
                        </div>
                    ) : (
                        filteredReports.map((report) => (
                            <div
                                key={report.id}
                                onClick={() => {
                                    hapticFeedback('light');
                                    onSelectReport(report);
                                }}
                                className="group p-3.5 rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50/70 dark:bg-white/[0.03] hover:bg-orange-500/5 dark:hover:bg-orange-500/10 hover:border-orange-500/30 transition-all duration-200 cursor-pointer flex items-center justify-between gap-3 shadow-sm"
                            >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <div className="w-9 h-9 rounded-xl bg-orange-500/10 dark:bg-orange-400/10 text-orange-600 dark:text-orange-400 flex items-center justify-center shrink-0 border border-orange-500/20">
                                        <FileText className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h4 className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 truncate group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                                            {report.name}
                                        </h4>
                                        <div className="flex flex-wrap items-center gap-2 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                                            <span className="flex items-center gap-1 font-medium text-slate-600 dark:text-slate-300">
                                                <Calendar className="w-3 h-3 text-slate-400" />
                                                {report.date}
                                            </span>
                                            {report.cargoSummary && (
                                                <>
                                                    <span>•</span>
                                                    <span className="truncate">{report.cargoSummary}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                    {/* Open/View button */}
                                    <button
                                        onClick={() => {
                                            hapticFeedback('light');
                                            onSelectReport(report);
                                        }}
                                        className="p-2 rounded-xl text-orange-600 dark:text-orange-400 hover:bg-orange-500/10 transition-colors"
                                        title={isTurkish ? 'Görüntüle' : 'View'}
                                    >
                                        <Eye className="w-4 h-4" />
                                    </button>

                                    {/* Direct Download button */}
                                    <a
                                        href={report.url}
                                        download={report.name}
                                        onClick={() => hapticFeedback('medium')}
                                        className="p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                                        title={isTurkish ? 'İndir' : 'Download'}
                                    >
                                        <Download className="w-4 h-4" />
                                    </a>

                                    {/* Delete button */}
                                    <button
                                        onClick={(e) => handleDelete(e, report.id)}
                                        className="p-2 rounded-xl text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                                        title={isTurkish ? 'Sil' : 'Delete'}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer Controls */}
                {reports.length > 0 && (
                    <div className="pt-3 border-t border-slate-200 dark:border-white/10 flex items-center justify-between gap-3">
                        <button
                            onClick={handleClearAll}
                            className="text-xs text-rose-500 hover:text-rose-600 font-semibold transition-colors flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-rose-500/10"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                            {isTurkish ? 'Tümünü Temizle' : 'Clear All'}
                        </button>
                        <button
                            onClick={() => { hapticFeedback('light'); onClose(); }}
                            className="px-5 py-2 bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 text-slate-800 dark:text-white font-bold text-xs rounded-xl transition-all"
                        >
                            {isTurkish ? 'Kapat' : 'Close'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
